import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import VideoCard from '@/components/feature/VideoCard';
import { videos } from '@/mocks/videos';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQ = searchParams.get('q') ?? '';
  const [keyword, setKeyword] = useState(initialQ);
  const [query, setQuery] = useState(initialQ);

  usePageMeta(
    query ? `“${query}”的搜索结果 - 涩涩视频` : '搜索视频 - 涩涩视频',
    '在涩涩视频搜索电影、短视频、娱乐等丰富多样的视频内容，快速定位感兴趣的精彩资源。',
  );

  useEffect(() => {
    setKeyword(initialQ);
    setQuery(initialQ);
  }, [initialQ]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return videos.filter((v) => {
      const haystack = [v.title, v.genre, v.categoryName, v.region, v.description, ...v.tags]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [query]);

  const hotRecommend = useMemo(
    () => [...videos].sort((a, b) => b.views - a.views).slice(0, 5),
    [],
  );

  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const q = keyword.trim();
    setQuery(q);
    setSearchParams(q ? { q } : {});
  };

  return (
    <Layout>
      <section className="border-b border-background-200/70 bg-background-200/50">
        <div className="mx-auto w-full px-4 py-8 md:px-8 md:py-12">
          <h1 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
            <i className="ri-search-2-line mr-2 text-primary-500" />
            搜索视频
          </h1>
          <p className="mt-2 text-xs text-foreground-500 md:text-sm">
            输入关键词，从电影、短视频、娱乐等海量内容中快速找到你感兴趣的精彩视频。
          </p>
          <form onSubmit={submit} className="mt-5 flex w-full max-w-2xl flex-col gap-3 sm:flex-row">
            <div className="flex flex-1 items-center rounded-full border border-background-300 bg-background-50 px-4 py-2.5">
              <i className="ri-search-line text-lg text-foreground-400" />
              <input
                name="keyword"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="试试搜索“科幻”“美食”“脱口秀”"
                className="ml-2 w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
              />
            </div>
            <button
              type="submit"
              className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </form>
        </div>
      </section>

      <div className="mx-auto w-full px-4 py-8 md:px-8 md:py-10">
        {query.trim() ? (
          results.length > 0 ? (
            <>
              <p className="mb-5 text-sm text-foreground-600">
                关于“<span className="font-medium text-foreground-950">{query}</span>”共找到{' '}
                <span className="font-semibold text-primary-600">{results.length}</span> 个视频
              </p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
                {results.map((v) => (
                  <VideoCard key={v.id} video={v} />
                ))}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-search-eye-line text-2xl" />
              </span>
              <h2 className="mt-4 font-heading text-lg font-bold text-foreground-900">
                没有找到与“{query}”相关的视频
              </h2>
              <p className="mt-2 max-w-sm text-sm text-foreground-500">
                换个关键词试试，或者看看下面的热门推荐，也许会有惊喜。
              </p>
              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setKeyword('');
                    setQuery('');
                    setSearchParams({});
                  }}
                  className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-refresh-line mr-1" />
                  重新搜索
                </button>
                <Link
                  to="/"
                  className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  返回首页
                </Link>
              </div>
            </div>
          )
        ) : (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-primary-500">
              <i className="ri-lightbulb-line text-2xl" />
            </span>
            <h2 className="mt-4 font-heading text-lg font-bold text-foreground-900">
              输入关键词开始搜索
            </h2>
            <p className="mt-2 max-w-sm text-sm text-foreground-500">
              支持按标题、标签、分类、简介匹配。搜索为前端演示效果，会在大致匹配的内容中为你展示结果。
            </p>
          </div>
        )}

        {/* Hot recommendations */}
        <section className="mt-12">
          <h2 className="mb-5 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
            <span className="h-5 w-1.5 rounded-full bg-accent-500" />
            热门推荐
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
            {hotRecommend.map((v, i) => (
              <VideoCard key={v.id} video={v} rank={i + 1} />
            ))}
          </div>
        </section>
      </div>
    </Layout>
  );
}